import { subscription } from 'api/subscriptions';
import { Button } from 'components/shadcn/button';
import ScreenTitle from 'components/Shared/ScreenTitle';
import OldButton from 'components/UI/Button';
import { IonIcon } from 'components/UI/IonIcon';
import Layout from 'components/UI/Layout';
import Loader from 'components/UI/Loader';
import { useQuery } from 'hooks/useQuery';
import { useLanguage } from 'languages/languageContext';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useAppDispatch, useAppSelector } from 'store';
import { hideResponse, showResponse } from 'store/actions/response';
import { getOwnerProfileRequest } from 'store/actions/users';
import { DeletedCard } from './components/DeletedCard';
import { Card } from './components/Card';
import { TPrice, TSubscription, TPlan } from './types';

const Subscription = () => {
  const { subscription: lSubscription } = useLanguage();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const history = useHistory();
  const [prices, setPrices] = useState([] as TPrice[]);
  const [subscriptions, setSubscriptions] = useState([] as TSubscription[]);
  const [loading, setLoading] = useState(false);
  const [publishableKey, setPublishableKey] = useState('');
  const [plans, setPlans] = useState([] as TPlan[]);
  const [skus, setSkus] = useState([] as string[]);
  const [otherSub, setOtherSub] = useState({
    expireDate: '',
    isActive: false,
    purchasePlatform: null as 'ios' | 'android',
    plan: null as TPlan,
    history: [],
  });

  const searchQuery = useQuery();
  const showSuccessModal = searchQuery.get('redirect_status') === 'succeeded';

  const activeSubscriptions = useMemo(() => subscriptions.filter((sub) => sub.status === 'active'), [subscriptions]);

  const activeSubscriptionRootId = useMemo(() => otherSub?.plan?.rootId || null, [otherSub?.plan?.rootId]);
  const activeSub = useMemo(
    () => plans.find((plan) => plan.type === 'stripe' && plan.rootId === activeSubscriptionRootId),
    [activeSubscriptionRootId, plans],
  );
  const activeSubId = useMemo(
    () => (moment(otherSub.expireDate).isAfter() ? activeSub?.id : null),
    [activeSub?.id, otherSub.expireDate],
  );

  const isActivePlanDisabled = useMemo(
    () => activeSubscriptions[0]?.id && otherSub.isActive && !activeSubId,
    [activeSubId, activeSubscriptions, otherSub.isActive],
  );

  const user = useAppSelector((state) => state.users.user);

  useEffect(() => {
    if (showSuccessModal) {
      dispatch(
        showResponse({ title: lSubscription.successfulPayment.title, message: lSubscription.successfulPayment.text }),
      );
      history.replace('/subscription/prices');
    }

    setLoading(true);

    const getConfig = async () => {
      const {
        prices,
        //@ts-ignore
        _doc: {
          stripe: { publishableKey },
          subscriptions: plans,
        },
      } = await subscription.config();
      const skus = plans?.reduce((acc, sub) => {
        if (!acc.some((p) => p === sub.id)) {
          acc.push(sub.id);
        }
        return acc;
      }, []);

      const filteredPrices = prices.filter((price) => skus.some((sku) => sku === price.product.id));

      setSkus(skus);
      setPlans(plans);
      setPublishableKey(publishableKey);
      setPrices(
        filteredPrices.filter((price) => (price.product.active && price.active) || activeSubId === price.product.id),
      );
    };

    const checkCustomer = async () => {
      try {
        let { customer } = await subscription.customer();

        await getSubscriptions();

        if (!customer) await subscription.createCustomer(localStorage.getItem('email'));
      } catch (err) {
        console.log('error ~ checkCustomer', err);
      }
    };

    checkCustomer();
    getConfig();

    setLoading(false);
  }, []);

  useEffect(() => {
    if (activeSubscriptions.length > 0 && user?.isGuest) {
      setTimeout(() => {
        const userId = localStorage.getItem('userId');
        dispatch(getOwnerProfileRequest(userId));
      }, 2000);
    }
  }, [activeSubscriptions.length, dispatch, user]);

  const getSubscriptions = useCallback(async () => {
    setLoading(true);
    const {
      subscriptions: { data: subscriptions },
      ...otherData
    } = await subscription.subscriptions();

    setOtherSub(otherData);

    setSubscriptions(subscriptions);
    setLoading(false);
  }, []);

  const createSubscription = useCallback(
    async (priceId: string) => {
      const { subscriptionId, clientSecret } = await subscription.createSubscription(priceId);

      history.push('/subscription/subscribe', {
        state: {
          from: location,
          subscriptionId,
          clientSecret,
          publishableKey,
        },
        replace: false,
      });
    },
    [history, location, publishableKey],
  );

  const getClientSecret = useCallback(
    async (priceId: string) => {
      const { clientSecret } = await subscription.getClientSecret(priceId);

      history.push('/subscription/subscribe', {
        state: {
          from: location,
          clientSecret,
          publishableKey,
        },
        replace: false,
      });
    },
    [history, location, publishableKey],
  );

  const cancelSubscription = useCallback(
    async (sub: string, redirectTo?: 'ios' | 'android' | 'stripe') => {
      dispatch(
        showResponse({
          title: lSubscription.cancel.title,
          message: lSubscription.cancel.text,
          options: [
            <OldButton
              label={lSubscription.cancel.cancel}
              onClick={async () => {
                if (redirectTo === 'ios') {
                  window.open('https://apps.apple.com/account/subscriptions');
                  dispatch(hideResponse());

                  return;
                }
                if (redirectTo === 'android') {
                  window.open('https://play.google.com/store/account/subscriptions');
                  dispatch(hideResponse());

                  return;
                }
                await subscription.cancelSubscription(sub);
                await getSubscriptions();
                dispatch(hideResponse());
              }}
            />,
          ],
        }),
      );
    },
    [dispatch, getSubscriptions, lSubscription.cancel.cancel, lSubscription.cancel.text, lSubscription.cancel.title],
  );

  const cancelHandler = (plan: TPlan) => {
    if (plan?.isOneTime) {
      dispatch(
        showResponse({
          title: lSubscription.cancel.title,
          message: lSubscription.cancel.cancelOneTime,
        }),
      );
      return;
    }
    cancelSubscription(activeSubId, otherSub.purchasePlatform);
  };

  return (
    <Layout>
      <ScreenTitle title={lSubscription.subscription} />
      {loading ? (
        <Loader isLoading={loading} />
      ) : (
        <div className="px-8 py-4 overflow-y-auto h-full max-h-screen min-h-0 pb-60">
          <div className="h-4" />
          <div className="flex flex-col gap-2">
            {isActivePlanDisabled && (
              <DeletedCard
                unit_amount={Number(otherSub.plan?.price)}
                // @ts-ignore
                recurring={{ interval: 'month', interval_count: (Number(otherSub.plan?.period) / 43200).toFixed(0) }}
                {...activeSubscriptions[0]?.plan}
                isActive={true}
                description={otherSub?.plan?.description}
                isOneTime={otherSub?.plan?.isOneTime}
                expireDate={otherSub.expireDate}
                cancelHandler={() => cancelHandler(otherSub.plan)}
              />
            )}

            {prices
              ?.sort((a, b) => {
                const aIsActive = activeSubId === a.product.id;
                const bIsActive = activeSubId === b.product.id;

                // First sort by active status
                if (aIsActive !== bIsActive) {
                  return aIsActive ? -1 : 1;
                }

                // Then sort by skus order
                const aIndex = skus.indexOf(a.product.id);
                const bIndex = skus.indexOf(b.product.id);
                return aIndex - bIndex;
              })
              .map((price) => {
                const isActive = activeSubId === price.product.id;
                const plan = plans.find((plan) => plan.id === price.product.id);
                const description = plan?.description;

                const isDisabled = !!otherSub?.history?.length && plan?.isOneTime && !isActive;

                const createSubscriptionHandler = async (priceId: string) => {
                  if (plan?.isOneTime) {
                    await getClientSecret(priceId);
                    return;
                  }
                  await createSubscription(priceId);
                };

                return (
                  <>
                    {isActive && (
                      <div className="flex flex-row items-center gap-2">
                        <IonIcon name="star" size={22} />
                        <h2 className="text-lg font-medium">{lSubscription.subscriptionActive}</h2>
                        <h2 className="text-sm/[30px]">
                          ({lSubscription.until} {moment(otherSub.expireDate).format('DD.MM.YYYY')})
                        </h2>
                      </div>
                    )}
                    <Card
                      {...price}
                      createSubscription={createSubscriptionHandler}
                      key={price.id}
                      isActive={isActive}
                      description={description}
                      isOneTime={plan?.isOneTime}
                      disabled={isDisabled}
                    />
                    {isActive && (
                      <Button className="bg-red-600 my-2" onClick={() => cancelHandler(plan)}>
                        {lSubscription.cancel.cancel}
                      </Button>
                    )}
                  </>
                );
              })}
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Subscription;
